import React, { useRef, useState, useEffect, ReactElement, cloneElement } from 'react';
import { useLang } from '../hooks/useLanguage';

type Props = {
    children: ReactElement<HTMLInputElement>;
};

const RangeSlider: React.FC<Props> = ({ children }) => {
    const { t } = useLang();
    const input = children;
    const { min = 0, max = 100, value, onChange, name } = input.props;
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef<HTMLDivElement>(null);

    const getPercent = (val: number) => ((val - Number(min)) / (Number(max) - Number(min))) * 100;

    const updateValueFromX = (clientX: number) => {
        if (!trackRef.current || !onChange) return;
        const rect = trackRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        let percent = x / rect.width;
        percent = Math.max(0, Math.min(1, percent));
        const newValue = Math.round(Number(min) + percent * (Number(max) - Number(min)));
        onChange({ target: { value: newValue, name } } as any);
    };

    useEffect(() => {
        const move = (e: MouseEvent | TouchEvent) => {
            const x = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
            updateValueFromX(x);
        };
        const stop = () => setIsDragging(false);

        if (isDragging) {
            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', stop);
            window.addEventListener('touchmove', move);
            window.addEventListener('touchend', stop);
        }

        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
            window.removeEventListener('touchmove', move);
            window.removeEventListener('touchend', stop);
        };
    }, [isDragging]);

    const percent = getPercent(numericValue);

    return (
        <div className="fireknife-range-slider">
            {cloneElement(input, {
                className: `${input.props.className ?? ''} fireknife-visually-hidden`.trim(),
            })}

            <div
                ref={trackRef}
                className={`fireknife-range-slider__track${isDragging ? ' fireknife-range-slider--dragging' : ''}`}
                style={{ ['--fireknife-range-slider-percent' as any]: `${percent}%` }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                    const target = e.target as HTMLElement;

                    if (!target.classList.contains('fireknife-range-slider__thumb')) {
                        updateValueFromX(e.clientX);
                    }
                }}
                onTouchStart={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                    const target = e.target as HTMLElement;
                    if (!target.classList.contains('fireknife-range-slider__thumb')) {
                        updateValueFromX(e.touches[0].clientX);
                    }
                }}
            >
                <div className="fireknife-range-slider__runnable-track" />
                <div className="fireknife-range-slider__thumb" />
                <div className="fireknife-range-slider__tooltip">
                    {new Intl.NumberFormat(t('widgetLocale')).format(numericValue)}
                </div>
            </div>
        </div>
    );
};

export default RangeSlider;
